import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/components/providers/supabase-auth-provider'
import { SUBSCRIPTION_TIERS, SubscriptionTier } from '@/config/subscriptions'
import { Subscription } from '@/lib/supabase'

type StripeDetails = {
  nextBillingDate: number | null; // Unix timestamp
  paymentMethodDetails: {
    brand: string | null;
    last4: string | null;
  } | null;
}

export function useSubscription() {
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [stripeDetails, setStripeDetails] = useState<StripeDetails | null>(null)
  const [tier, setTier] = useState<SubscriptionTier>(SUBSCRIPTION_TIERS.free as SubscriptionTier)
  const [isLoading, setIsLoading] = useState(true)
  const [isDetailsLoading, setIsDetailsLoading] = useState(false)
  const { user, session } = useAuth()

  const fetchStripeDetails = useCallback(async (customerId: string) => {
    if (!session) return;
    setIsDetailsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-stripe-subscription-details', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) {
        throw error;
      }
      
      setStripeDetails(data as StripeDetails);
    } catch (error) {
      console.error('Error fetching Stripe details:', error);
      setStripeDetails(null);
    } finally {
      setIsDetailsLoading(false);
    }
  }, [session]);

  const loadSubscription = useCallback(async () => {
    try {
      setIsLoading(true);
      setStripeDetails(null);

      if (!user) {
        setTier(SUBSCRIPTION_TIERS.free as SubscriptionTier);
        setSubscription(null);
        return;
      }

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          setTier(SUBSCRIPTION_TIERS.free as SubscriptionTier);
          setSubscription(null);
        } else {
          console.error('Error fetching subscription:', error);
          setTier(SUBSCRIPTION_TIERS.free as SubscriptionTier);
          setSubscription(null);
        }
      } else if (data) {
        setSubscription({ ...data, status: data.status as 'active' | 'canceled' | 'past_due' });
        const validTier = Object.values(SUBSCRIPTION_TIERS).includes(data.tier as any)
          ? data.tier as SubscriptionTier
          : SUBSCRIPTION_TIERS.free as SubscriptionTier;
        setTier(validTier);

        if (data.stripe_customer_id) {
          fetchStripeDetails(data.stripe_customer_id);
        }
      } else {
        setTier(SUBSCRIPTION_TIERS.free as SubscriptionTier);
        setSubscription(null);
      }
    } catch (error) {
      console.error('Unexpected error in loadSubscription:', error);
      setTier(SUBSCRIPTION_TIERS.free as SubscriptionTier);
      setSubscription(null);
    } finally {
      setIsLoading(false);
    }
  }, [user, fetchStripeDetails]);

  useEffect(() => {
    loadSubscription();

    const subscriptionChannel = supabase
      .channel('subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user?.id}`,
        },
        (payload) => {
          console.log('Subscription changed, reloading:', payload);
          loadSubscription();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscriptionChannel);
    };
  }, [user, loadSubscription]);

  return {
    subscription,
    stripeDetails,
    tier,
    isLoading: isLoading || isDetailsLoading,
  };
} 